import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Button from '@material-ui/core/Button'
import AltaFirmwareModal from 'components/AltaFirmwareModal/AltaFirmwareModal'
import CustomSelect from 'components/CustomSelect/CustomSelect'
import Error from 'components/Error/Error'
import Loader from 'components/Loader/Loader'
import NoData from 'components/NoData/NoData'
import PageTitle from 'components/PageTitle/PageTitle'
import Table from 'components/Table/Table'
import useCallService from 'services/useCallService'
import { GET_ALTA_FIRMWARE_URL, getOemOptionsForPartner } from 'Constants'
import useStyles from './styles'

const AltaFirmwarePanel = () => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const { callGatewayService } = useCallService()
  const [altaFirmwaresList, setAltaFirmwareList] = useState([])
  const [parameters, setParameters] = useState({ sortBy: 'firmwareVersion', sorting: 'ASC' })
  const [openModal, setOpenModal] = React.useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const oemOptions = getOemOptionsForPartner()
  const [selectedOemId, setSelectedOemId] = useState(oemOptions.length === 1 ? oemOptions[0].value : '')

  useEffect(() => {
    loadAltaFirmwareTable()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameters, selectedOemId])

  const loadAltaFirmwareTable = () => {
    async function fetchAltaControllers() {
      setIsLoading(true)
      setIsError(false)
      const oemQuery = selectedOemId !== '' ? `&oemId=${selectedOemId}` : ''
      return callGatewayService(
        'GET',
        `${GET_ALTA_FIRMWARE_URL}?brand=${process.env.REACT_APP_PARTNER_NAME}${oemQuery}&sortBy=${parameters.sortBy}&sorting=${parameters.sorting}`,
        {},
        true
      )
    }

    fetchAltaControllers()
      .then((response) => {
        setAltaFirmwareList(response.data)
      })
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false))
  }

  const handleSort = (order, orderBy) => {
    setParameters({
      sortBy: orderBy,
      sorting: order.toUpperCase()
    })
  }

  const handleOpenModal = () => {
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const filterForm = {
    values: {
      oemId: selectedOemId
    },
    touched: {},
    errors: {},
    handleChange: (event) => setSelectedOemId(event.target.value),
    handleBlur: () => null
  }

  const columns = [
    {
      id: 'firmwareVersion',
      sorting: true,
      label: formatMessage({ id: 'AltaFirmware.column.firmwareVersion' }),
      width: classes.smallWidth
    },
    {
      id: 'createdDate',
      sorting: true,
      label: formatMessage({ id: 'AltaFirmware.column.releaseDate' }),
      type: 'date',
      width: classes.smallWidth
    },
    {
      id: 'releaseNotes',
      sorting: true,
      label: formatMessage({ id: 'AltaFirmware.column.releaseNotes' }),
      className: classes.wrapText,
      width: classes.mediumWidth
    },
    {
      id: 'createdBy',
      sorting: true,
      label: formatMessage({ id: 'AltaFirmware.column.uploadedBy' }),
      width: classes.smallWidth
    }
  ]

  return (
    <div className={classes.container}>
      <div className={classes.containerHeader}>
        <PageTitle titleId={'AltaFirmware.panel.title'} />
        <div className={classes.actionsContainer}>
          <div className={classes.oemSelect}>
            <CustomSelect
              form={filterForm}
              formFieldName={'oemId'}
              labelId={'AltaFirmware.panel.oemFilter'}
              items={oemOptions}
              disabled={oemOptions.length <= 1}
            />
          </div>
          <Button type={'button'} className={classes.button} onClick={handleOpenModal}>
            <FormattedMessage id={'AltaFirmware.panel.uploadNewFirmware'} />
          </Button>
        </div>
      </div>
      <div className={classes.dataContainer}>
        {isError && <Error />}
        {isLoading && <Loader />}
        {!isError && !isLoading && altaFirmwaresList.length === 0 && <NoData />}
        {!isError && !isLoading && altaFirmwaresList.length > 0 && (
          <Table
            columns={columns}
            data={altaFirmwaresList}
            defaultOrderKey={parameters.sortBy}
            defaultOrder={parameters.sorting.toLowerCase()}
            onSortChange={handleSort}
            className={{ table: classes.table }}
          />
        )}
      </div>
      <AltaFirmwareModal
        open={openModal}
        handleClose={handleCloseModal}
        onSuccess={loadAltaFirmwareTable}
        selectedOemId={selectedOemId}
        title={formatMessage({ id: 'AltaFirmware.modal.title' })}
      />
    </div>
  )
}

export default AltaFirmwarePanel
